import React, { useState, useEffect, useCallback } from 'react';
import './ShipmentTracker.css';

const ShipmentTracker = ({ shipmentId, orderId }) => {
  // Use orderId as fallback if shipmentId is not available
  const trackingId = shipmentId || orderId;
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchTrackingData = useCallback(async () => {
    if (!trackingId) return;
    
    setLoading(true);
    setError(null);
    
    try {
              const response = await fetch(`https://shiprocket-backend-maeh.onrender.com/track-shipment/${trackingId}`);
      const result = await response.json();
      
      if (result.success) {
        setTrackingData(result.data);
      } else {
        setError(result.error || 'Failed to fetch tracking data');
      }
    } catch (err) {
      setError('Network error while fetching tracking data');
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (trackingId) {
      fetchTrackingData();
    }
  }, [trackingId, fetchTrackingData]);

  useEffect(() => {
    let interval;
    if (autoRefresh && trackingId) {
      interval = setInterval(fetchTrackingData, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, trackingId, fetchTrackingData]);

  if (!trackingId) {
    return <div className="tracker-no-data">No tracking ID available</div>;
  }

  if (loading) {
    return <div className="tracker-loading">🔄 Loading tracking information...</div>;
  }

  if (error) {
    return (
      <div className="tracker-error">
        <p>❌ Error: {error}</p>
        <button onClick={fetchTrackingData} className="retry-button">
          🔄 Retry
        </button>
      </div>
    );
  }

  if (!trackingData) {
    return <div className="tracker-no-data">No tracking data available</div>;
  }

  return (
    <div className="shipment-tracker" id={`tracking-${shipmentId}`}>
      <div className="tracker-header">
        <h4>📦 Real-Time Shipment Tracking</h4>
        <div className="tracker-controls">
          <button 
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`auto-refresh-button ${autoRefresh ? 'active' : ''}`}
          >
            {autoRefresh ? '🔄 Auto-refresh ON' : '⏸️ Auto-refresh OFF'}
          </button>
          <button 
            onClick={fetchTrackingData}
            className="refresh-button"
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="tracking-info">
        <div className="tracking-item">
          <strong>🚚 Tracking ID:</strong> {trackingId}
        </div>
        {trackingData.shipment_id && (
          <div className="tracking-item">
            <strong>📋 Shiprocket ID:</strong> {trackingData.shipment_id}
          </div>
        )}
        {trackingData.awb && (
          <div className="tracking-item">
            <strong>📄 AWB Number:</strong> {trackingData.awb}
          </div>
        )}
        {trackingData.courier_name && (
          <div className="tracking-item">
            <strong>🚛 Courier:</strong> {trackingData.courier_name}
          </div>
        )}
        {trackingData.status && (
          <div className="tracking-item">
            <strong>📊 Status:</strong> 
            <span className={`status-badge status-${trackingData.status.toLowerCase()}`}>
              {trackingData.status}
            </span>
          </div>
        )}
        {trackingData.etd && (
          <div className="tracking-item">
            <strong>📅 Expected Delivery:</strong> {new Date(trackingData.etd).toLocaleDateString()}
          </div>
        )}
      </div>
      
      {trackingData.track_data && trackingData.track_data.length > 0 && (
        <div className="tracking-timeline">
          <h5>📈 Tracking Timeline</h5>
          <div className="timeline">
            {trackingData.track_data.map((event, index) => (
              <div key={index} className={`timeline-item ${index === 0 ? 'latest' : ''}`}>
                <div className="timeline-date">
                  {new Date(event.date).toLocaleDateString()}
                  <div className="timeline-time">
                    {new Date(event.date).toLocaleTimeString()}
                  </div>
                </div>
                <div className="timeline-content">
                  <div className="timeline-location">📍 {event.location}</div>
                  <div className="timeline-status">{event.status}</div>
                  {event.activity && (
                    <div className="timeline-activity">{event.activity}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="tracking-actions">
        <button 
          onClick={() => {
            const trackingUrl = `https://www.shiprocket.in/tracking/${shipmentId}`;
            window.open(trackingUrl, '_blank');
          }}
          className="external-track-button"
        >
          🔗 View on Shiprocket (External)
        </button>
      </div>
    </div>
  );
};

export default ShipmentTracker; 